import { formatTime, formatOz } from "./dashboard_utils";

const AXIS_STEP_MINS = 15;
const CHART_HEIGHT = 80;
const BAR_WIDTH = 20;
const Y_MAX_OZ = 3.5;
const TARGET_OZ = 2.5;

const FeedingBarChart = ({
  feedings,
  windowHours,
}: {
  feedings: Feeding[];
  windowHours: number;
}) => {
  const now = Date.now();
  const windowMs = windowHours * 60 * 60 * 1000;
  const windowMins = windowHours * 60;
  const barsWithAmount = feedings.filter(
    (f) => f.in_window && f.amount_oz != null,
  );

  // Helper: returns [{label, pct, align}]
  function getXAxisLabels() {
    const increments = windowMins / AXIS_STEP_MINS; // one segment per AXIS_STEP_MINS across the window
    let labels = [];
    for (let i = 0; i <= increments; i++) {
      const minsAgo = windowMins - i * AXIS_STEP_MINS;
      let label;
      if (minsAgo === 0) {
        label = "now";
      } else if (minsAgo >= 60) {
        const h = Math.floor(minsAgo / 60);
        const m = minsAgo % 60;
        label = m === 0 ? `${h}h` : `${h}h:${m.toString().padStart(2, "0")}m`;
      } else {
        label = `${minsAgo}m`;
      }
      const pct = (i / increments) * 100;
      let align = "center";
      if (i === 0) align = "left";
      if (i === increments) align = "right";
      labels.push({ label, pct, align });
    }
    return labels;
  }

  return (
    <div style={{ padding: "0 4px", marginTop: 16 }}>
      <div
        style={{
          position: "relative",
          height: CHART_HEIGHT,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          borderLeft: "1px solid rgba(0,0,0,0.12)",
          overflow: "visible",
        }}
      >
        {/* Removed target reference line */}

        {barsWithAmount.length === 0 ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              no amounts recorded
            </span>
          </div>
        ) : (
          barsWithAmount.map((feeding) => {
            const elapsed = now - new Date(feeding.ended_at).getTime();
            const xPct = (1 - elapsed / windowMs) * 100;
            const hPct = Math.min((feeding.amount_oz! / Y_MAX_OZ) * 100, 100);
            const barLeft = `calc(${xPct}% - ${BAR_WIDTH / 2}px)`;
            const barHeight = `${hPct}%`;
            const barBottom = 0;

            // Calculate position for amount label
            // Amount label should be horizontally centered above the bar
            // and positioned a few px above the bar's top edge
            const amountLabelBottom = `calc(${barHeight} + 4px)`;

            // Adjust horizontal centering with left: 50% and transform: translateX(-50%)
            return (
              <div key={feeding.id}>
                <span
                  style={{
                    position: "absolute",
                    left: `calc(${xPct}% )`, // set left to horizontal center of bar
                    bottom: amountLabelBottom,
                    minWidth: BAR_WIDTH,
                    textAlign: "center",
                    fontSize: "1.4em",
                    color: "var(--bs-primary)",
                    fontWeight: 700,
                    pointerEvents: "none",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                    transform: "translateX(-50%)",
                  }}
                >
                  {formatOz(feeding.amount_oz!)}
                </span>
                <div
                  title={`${feeding.amount_oz} oz at ${formatTime(feeding.ended_at)}`}
                  style={{
                    position: "absolute",
                    bottom: barBottom,
                    left: barLeft,
                    width: BAR_WIDTH,
                    height: barHeight,
                    minHeight: 4,
                    background: "var(--bs-primary)",
                    borderRadius: "3px 3px 0 0",
                    opacity: 0.8,
                  }}
                />
              </div>
            );
          })
        )}
      </div>

      {/* X-axis labels */}
      <div style={{ position: "relative", height: 40, marginTop: 4 }}>
        {getXAxisLabels().map(({ label, pct, align }) => {
          let transform;
          let left;
          let styleAlign;
          if (align === "left") {
            left = `0%`;
            transform = "rotate(-90deg) translateY(-100%)";
            styleAlign = "left";
          } else if (align === "right") {
            left = `100%`;
            transform = "rotate(-90deg) translateY(-100%)";
            styleAlign = "right";
          } else {
            left = `${pct}%`;
            transform = "translateX(-50%) rotate(-90deg) translateY(-100%)";
            styleAlign = "center";
          }
          return (
            <span
              key={label + pct}
              style={{
                position: "absolute",
                left,
                top: "90%", // Push labels below the chart
                fontSize: "0.7rem",
                color: "var(--bs-secondary)",
                transform,
                transformOrigin: "top",
                whiteSpace: "nowrap",
                ...(styleAlign === "left" && { textAlign: "left" }),
                ...(styleAlign === "right" && { textAlign: "right" }),
                ...(styleAlign === "center" && { textAlign: "center" }),
              }}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

type Feeding = {
  id: number;
  ended_at: string;
  amount_oz: number | null;
  in_window: boolean;
};

type PastFeedingsSummary = {
  window_hours: number;
  window_total_oz: number;
  window_count: number;
  gauge_ratio: number;
  has_amounts: boolean;
  feedings: Feeding[];
};

type Props = {
  pastFeedings: PastFeedingsSummary;
};

export const PastFeedingsWidget = ({ pastFeedings }: Props) => {
  const { window_hours, window_total_oz, has_amounts, feedings } =
    pastFeedings;

  return (
    <div className="col-12 col-md-6">
      <div className="card border-0 shadow bg-gray-3 my-4 dashboard-card dashboard-card-past-feeding">
        <div className="card-body text-center">
          <div className="fs-24 fw-bold text-muted text-uppercase">
            Last {window_hours} {window_hours === 1 ? "Hour" : "Hours"}
          </div>

          <div className="fs-48 desktop-fs-90 fw-bold mt-2">
            {formatOz(window_total_oz)} oz
          </div>

          <FeedingBarChart feedings={feedings} windowHours={window_hours} />
        </div>
      </div>
    </div>
  );
};
